import React from 'react';
import { MapPin } from 'lucide-react';
import ServiceWorkspaceSidePanel from './ServiceWorkspaceSidePanel';

/**
 * Workspace chrome: sticky breadcrumb header, optional full-width strip (`aboveColumns`),
 * then main + sidebar: one vertical scroll; rail column stretches in grid, inner stack uses sticky + self-start (no JS).
 */
const CaseWorkspaceShell = ({
  children,
  aboveColumns,
  caseNumber,
  caseStatusLabel,
  caseStatusBadgeClass,
  onBreadcrumbServices,
  trackVendorEnabled,
  trackVendorTitle,
  onTrackVendor,
  serviceId,
  detailedServiceData,
  loadingDetailedService,
  paymentRefreshKey = 0,
}) => {
  return (
    <div className="min-w-0 max-w-none pb-6 font-sans antialiased">
      <div className="mx-auto w-full max-w-[1480px] px-4 pt-3 sm:px-6 sm:pt-4 lg:px-8">
        <header className="cwp-sticky-header mb-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <nav
                className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] leading-snug text-slate-600"
                aria-label="Breadcrumb"
              >
                <button
                  type="button"
                  onClick={onBreadcrumbServices}
                  className="shrink-0 font-medium text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Services
                </button>
                <span className="text-slate-400" aria-hidden>
                  &gt;
                </span>
                <span className="min-w-0 truncate font-mono text-[13px] font-semibold text-slate-900">{caseNumber}</span>
                <span className="text-slate-400" aria-hidden>
                  &gt;
                </span>
                <span
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ring-1 ring-inset ${caseStatusBadgeClass}`}
                >
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-80" aria-hidden />
                  {caseStatusLabel}
                </span>
              </nav>
            </div>

            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 sm:gap-3">
              {trackVendorEnabled ? (
                <button
                  type="button"
                  onClick={onTrackVendor}
                  title={trackVendorTitle}
                  className="cwp-btn-primary inline-flex h-9 shrink-0 items-center gap-2 rounded-lg px-3.5 text-[13px] font-semibold shadow-sm"
                >
                  <MapPin className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  Track vendor
                </button>
              ) : (
                <span
                  title={trackVendorTitle}
                  aria-disabled="true"
                  className="inline-flex h-9 shrink-0 cursor-not-allowed items-center gap-2 rounded-lg bg-slate-200 px-3.5 text-[13px] font-semibold text-slate-500 shadow-none"
                >
                  <MapPin className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  Track vendor
                </span>
              )}
            </div>
          </div>
        </header>

        {aboveColumns ? <div className="mb-5 w-full min-w-0">{aboveColumns}</div> : null}

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-w-0 space-y-4">{children}</div>
          <aside className="cwp-workspace-rail-column min-w-0 xl:flex xl:flex-col xl:min-h-0">
            <div className="space-y-4 xl:sticky xl:self-start xl:w-full xl:top-[var(--cwp-rail-sticky-top)]">
              <ServiceWorkspaceSidePanel
                serviceId={serviceId}
                detailedServiceData={detailedServiceData}
                loadingDetailedService={loadingDetailedService}
                paymentRefreshKey={paymentRefreshKey}
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CaseWorkspaceShell;
